import { useRef, useState, type ReactNode } from "react";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { ComposerToolbar } from "./composer-toolbar";

const looksLikeEmail = (v: string) => /.+@.+\..+/.test(v.trim());

/** Frappe-style email composer: TO chips + CC/BCC + subject + formatted body.
    Sends for real through POST /crm/leads/{id}/emails/send (SQS queue). */
export function EmailComposer({ leadId, defaultTo, defaultSubject, onSent, onDiscard }: {
  leadId: string;
  defaultTo: string[];
  defaultSubject: string;
  onSent: () => void;
  onDiscard: () => void;
}) {
  const [toEmails, setToEmails] = useState<string[]>(defaultTo);
  const [toDraft, setToDraft] = useState("");
  const [ccOpen, setCcOpen] = useState(false);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccDraft, setCcDraft] = useState("");
  const [bccOpen, setBccOpen] = useState(false);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [bccDraft, setBccDraft] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const addChip = (list: string[], setList: (v: string[]) => void, draft: string, setDraft: (v: string) => void) => {
    const v = draft.trim();
    if (!v || list.includes(v)) return;
    if (!looksLikeEmail(v)) { setError(`"${v}" is not a valid email address.`); return; }
    setList([...list, v]); setDraft(""); setError("");
  };

  const chipRow = (
    label: string,
    list: string[],
    setList: (v: string[]) => void,
    draft: string,
    setDraft: (v: string) => void,
    placeholder: string,
    actions?: ReactNode,
  ) => (
    <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{label}:</span>
      {list.map(addr => (
        <span key={addr} className="flex items-center gap-1 rounded-full bg-soft px-2.5 py-1 text-xs font-medium text-ink">
          {addr}
          <button onClick={() => setList(list.filter(a => a !== addr))} aria-label={`Remove ${addr}`} className="text-ink-3 hover:text-red-600">×</button>
        </span>
      ))}
      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addChip(list, setList, draft, setDraft); } }}
        onBlur={() => { if (draft.trim()) addChip(list, setList, draft, setDraft); }}
        placeholder={list.length ? "" : placeholder}
        className="min-w-[140px] flex-1 bg-transparent text-sm text-ink outline-none"
      />
      {actions}
    </div>
  );

  const send = async () => {
    if (busy) return;
    if (toEmails.length === 0) { setError("Add at least one recipient."); return; }
    if (!body.trim()) { setError("Write the email body."); return; }
    setBusy(true); setError("");
    try {
      await crmApi.sendEmail(leadId, {
        to: toEmails,
        cc: ccEmails,
        bcc: bccEmails,
        subject: subject.trim() || defaultSubject,
        body: body.trim(),
      });
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to send email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-line bg-elevated">
      {chipRow("To", toEmails, setToEmails, toDraft, setToDraft, "name@company.com",
        <>
          <button onClick={() => setCcOpen(o => !o)} className={cn("rounded-md px-1.5 py-0.5 text-[11px] font-semibold", ccOpen ? "bg-soft text-ink" : "text-ink-3 hover:text-ink")}>CC</button>
          <button onClick={() => setBccOpen(o => !o)} className={cn("rounded-md px-1.5 py-0.5 text-[11px] font-semibold", bccOpen ? "bg-soft text-ink" : "text-ink-3 hover:text-ink")}>BCC</button>
        </>
      )}
      {ccOpen && chipRow("Cc", ccEmails, setCcEmails, ccDraft, setCcDraft, "cc@company.com")}
      {bccOpen && chipRow("Bcc", bccEmails, setBccEmails, bccDraft, setBccDraft, "bcc@company.com")}
      <div className="flex items-center gap-2 border-b border-line px-3 py-1.5">
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-ink-3">Subject:</span>
        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder={defaultSubject}
          className="min-w-0 flex-1 bg-transparent text-xs font-medium text-ink outline-none"
        />
      </div>
      <textarea
        ref={bodyRef}
        value={body}
        onChange={e => { setBody(e.target.value); setError(""); }}
        placeholder="Write the email…"
        rows={4}
        maxLength={10000}
        aria-label="Email body"
        className="w-full resize-y bg-transparent px-3 py-2 text-xs text-ink outline-none"
      />
      <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-1.5">
        <ComposerToolbar targetRef={bodyRef} value={body} onChange={v => { setBody(v); setError(""); }} />
        <div className="flex gap-2">
          <button onClick={onDiscard} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-ink-2 hover:bg-soft">Discard</button>
          <button disabled={busy || !body.trim() || toEmails.length === 0} onClick={() => void send()} className="rounded-lg bg-brand px-3 py-1 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs transition-colors disabled:opacity-40">
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
      {error && <p role="alert" className="px-3 pb-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}
